import { useState, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { DataGridPro, GridActionsCellItem } from "@mui/x-data-grid-pro";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import Add from "@mui/icons-material/Add";
import Edit from "@mui/icons-material/Edit";
import FileCopy from "@mui/icons-material/FileCopy";
import EventBusy from "@mui/icons-material/EventBusy";
import dayjs from "dayjs";
import { useGet } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { sortingModes } from "../../actprep-ui/constants/dataGrid";
import useDisclosure from "../../actprep-ui/hooks/ui/useDisclosure";
import RoleDrawer from "./RoleDrawer";
import { useConfirm } from "material-ui-confirm";
import api from "../../actprep-ui/utils/api";
import {
  showSuccessSnackbar,
  showErrorSnackbar,
} from "../../actprep-ui/utils/feedback";

export default function RolesTab({ coach, asOfDate, onDataChange }) {
  const gridApiRef = useGridApiRef();
  const confirm = useConfirm();

  const [selectedRole, setSelectedRole] = useState(null);
  const {
    isOpen: drawerOpen,
    onOpen: openDrawer,
    onClose: closeDrawer,
  } = useDisclosure();

  // Fetch role assignments for this coach
  const {
    data: rolesData,
    isLoading,
    refetch,
  } = useGet(apiUrls.coaching.roleAssignments(), {
    params: {
      search_params: {
        coach_id: coach?.uuid,
      },
      keep_related: true,
      order_by: ["-start_date"],
    },
    enabled: !!coach?.uuid,
  });
  const totalCount = rolesData?.total_count ?? 0;
  const roles = rolesData?.data || [];
  const handleEditRole = useCallback(
    (role) => {
      setSelectedRole(role);
      openDrawer();
    },
    [openDrawer]
  );
  const handleAddRole = () => {
    setSelectedRole(null);
    openDrawer();
  };

  const handleDuplicateRole = useCallback(
    (role) => {
      const duplicatedRole = {
        ...role,
        uuid: undefined,
        start_date: dayjs().format("YYYY-MM-DD"),
        end_date: null,
      };
      setSelectedRole(duplicatedRole);
      openDrawer();
    },
    [openDrawer]
  );

  const handleEndRole = useCallback(
    async (role) => {
      try {
        await confirm({
          title: "End Role Assignment",
          description: `Are you sure you want to end the ${role.role} assignment today?`,
          confirmationText: "End Today",
          cancellationText: "Cancel",
        });

        const response = await api.put(
          apiUrls.coaching.roleAssignments(role.uuid),
          {
            end_date: asOfDate.format("YYYY-MM-DD"),
          }
        );

        // Check the success field in the response
        if (response.data.success === false) {
          showErrorSnackbar(
            response.data.reason ||
              "Failed to end role assignment due to data validation issues"
          );
          return;
        }

        showSuccessSnackbar("Role assignment ended successfully");
        refetch();
        onDataChange?.();
      } catch (error) {
        if (error !== undefined) {
          // User didn't cancel
          showErrorSnackbar("Failed to end role assignment");
        }
      }
    },
    [confirm, asOfDate, refetch, onDataChange]
  );

  const handleDrawerSave = () => {
    refetch();
    closeDrawer();
    onDataChange?.();
  };

  // Check if a role is active as of the selected date
  const isRoleActiveAsOf = useCallback((role, date) => {
    const startDate = dayjs(role.start_date);
    const endDate = role.end_date ? dayjs(role.end_date) : null;
    return (
      role.is_active &&
      (date.isAfter(startDate, "day") || date.isSame(startDate, "day")) &&
      (!endDate || date.isBefore(endDate, "day") || date.isSame(endDate, "day"))
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "role",
        headerName: "Role",
        width: 180,
        renderCell: (params) => (
          <Chip
            label={params.value}
            color="primary"
            variant="outlined"
            size="small"
          />
        ),
      },
      {
        field: "start_date",
        headerName: "Start Date",
        width: 120,
        valueGetter: (value) => dayjs(value),
        valueFormatter: (value) => value.format("MM/DD/YYYY"),
      },
      {
        field: "end_date",
        headerName: "End Date",
        width: 120,
        valueGetter: (value) => (value ? dayjs(value) : null),
        valueFormatter: (value) =>
          value ? value.format("MM/DD/YYYY") : "Ongoing",
        renderCell: (params) => (
          <Chip
            label={params.value ? params.value.format("MM/DD/YYYY") : "Ongoing"}
            color={params.value ? "default" : "success"}
            variant="outlined"
            size="small"
          />
        ),
      },
      {
        field: "active_as_of",
        headerName: "Active @AsOf",
        width: 120,
        valueGetter: (value, row) => isRoleActiveAsOf(row, asOfDate),
        renderCell: (params) => (
          <Chip
            label={params.value ? "Active" : "Inactive"}
            color={params.value ? "success" : "default"}
            variant={params.value ? "filled" : "outlined"}
            size="small"
          />
        ),
      },
      {
        field: "is_active",
        headerName: "Status",
        width: 100,
        renderCell: (params) => (
          <Chip
            label={params.value ? "Active" : "Disabled"}
            color={params.value ? "success" : "error"}
            variant="outlined"
            size="small"
          />
        ),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 120,
        getActions: (params) => [
          <Tooltip key="edit-tooltip" title="Edit role assignment details">
            <GridActionsCellItem
              key="edit"
              icon={<Edit />}
              label="Edit"
              onClick={() => handleEditRole(params.row)}
            />
          </Tooltip>,
          <Tooltip
            key="duplicate-tooltip"
            title="Create a new role assignment based on this one"
          >
            <GridActionsCellItem
              key="duplicate"
              icon={<FileCopy />}
              label="Duplicate"
              onClick={() => handleDuplicateRole(params.row)}
            />
          </Tooltip>,
          <Tooltip
            key="end-tooltip"
            title={
              !params.row.is_active
                ? "Role assignment is disabled"
                : params.row.end_date
                ? "Role assignment already has an end date"
                : "End this role assignment today"
            }
          >
            <span>
              <GridActionsCellItem
                key="end"
                icon={<EventBusy />}
                label="End Today"
                onClick={() => handleEndRole(params.row)}
                disabled={!params.row.is_active || !!params.row.end_date}
              />
            </span>
          </Tooltip>,
        ],
      },
    ],
    [
      asOfDate,
      isRoleActiveAsOf,
      handleEditRole,
      handleDuplicateRole,
      handleEndRole,
    ]
  );

  // Apply row styling based on active status
  const getRowClassName = (params) => {
    const isActive = isRoleActiveAsOf(params.row, asOfDate);
    return isActive ? "" : "inactive-row";
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box />
        <Button variant="contained" startIcon={<Add />} onClick={handleAddRole}>
          Add Role
        </Button>
      </Stack>

      <Box sx={{ height: 600, width: "100%" }}>
        <DataGridPro
          apiRef={gridApiRef}
          rows={roles}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.uuid}
          getRowClassName={getRowClassName}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          sortingMode={sortingModes.SERVER}
          disableCellFocus
          showToolbar={false}
          sx={{
            "& .inactive-row": {
              opacity: 0.6,
              "& .MuiDataGrid-cell": {
                color: "text.secondary",
              },
            },
          }}
        />
      </Box>

      <RoleDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        role={selectedRole}
        coach={coach}
        asOfDate={asOfDate}
        onSave={handleDrawerSave}
      />
    </Box>
  );
}
